import { useState, useEffect } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import useConfirm from "@/hooks/useConfirm";
import {
  Target, Clock, Mail, Plus, Sparkles, Play, Loader2, CheckCircle2,
  AlertTriangle, X, Bot, Send, UserPlus, Calendar, RefreshCw, Handshake,
  RotateCcw, FileCheck, FileText, Edit3, Trash2, Phone, MessageSquare,
  Save, ChevronDown, ChevronUp, Settings2, Zap
} from "lucide-react";

const ICON_MAP = {
  UserPlus, Calendar, RefreshCw, Clock, Send, FileCheck, Handshake, RotateCcw, FileText,
  Mail, Phone, MessageSquare, Bot, Target, Zap, Sparkles,
};

const CHANNELS = [
  { id: "email", label: "Email", icon: Mail, color: "var(--blue)" },
  { id: "sms", label: "SMS / Text", icon: MessageSquare, color: "var(--green)" },
  { id: "call", label: "Outbound Call", icon: Phone, color: "var(--purple)" },
];

const TONES = ["Professional", "Friendly", "Empathetic", "Urgent", "Casual", "Formal", "Consultative"];

const TRIGGERS = [
  "Manual (run on demand)",
  "Lead saved with stage = New Leads",
  "Sub-status → Demo scheduled",
  "Sub-status → Demo rescheduled",
  "Sub-status → Demo completed",
  "Stage → Closed Won",
  "Stage → Closed Lost (after 90 days)",
  "Daily — Proposal stage, 5+ days",
  "Daily — Quotes expiring in 7 days",
  "Every Monday 9:00 AM",
  "Every Friday 3:00 PM",
  "Custom schedule",
];

const TARGET_AUDIENCES = [
  "All leads", "New leads only", "Stale leads (7+ days)",
  "Demo stage leads", "Proposal stage leads", "Decision stage leads",
  "Closed lost leads", "Live clients", "Hot leads (score 71+)",
  "Cold leads (score <41)", "Custom segment",
];

/* ─── Agent Builder Modal ─── */
function AgentBuilderModal({ agent, onSave, onClose }) {
  const { token } = useAuth();
  const [form, setForm] = useState({
    name: agent?.name || "",
    channel: agent?.channel || "email",
    description: agent?.description || "",
    tone: agent?.tone || "Professional",
    prompt_template: agent?.prompt_template || "",
    trigger: agent?.trigger || "Manual (run on demand)",
    target_audience: agent?.target_audience || "All leads",
    schedule: agent?.schedule || "",
    active: agent?.active !== undefined ? agent.active : true,
    icon_color: agent?.icon_color || "#0071e3",
    selected_leads: agent?.selected_leads || [],
    segment_name: agent?.segment_name || "",
  });
  const [leadSearch, setLeadSearch] = useState("");
  const [allLeads, setAllLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  // Section 11C — Natural language agent generation
  const [nlDescription, setNlDescription] = useState("");
  const [nlLoading, setNlLoading] = useState(false);

  const generateAgentFromDescription = async () => {
    if (!nlDescription.trim()) {
      toast.message("Type a description first.");
      return;
    }
    setNlLoading(true);
    try {
      const res = await axios.post(`${API}/ai/agent-from-description`,
        { description: nlDescription },
        { headers: { Authorization: `Bearer ${token}` } });
      const d = res.data || {};
      setForm(prev => ({
        ...prev,
        name: d.name || prev.name,
        channel: d.channel || prev.channel,
        tone: d.tone || prev.tone,
        trigger: d.trigger || prev.trigger,
        target_audience: d.target_audience || prev.target_audience,
        prompt_template: d.prompt_template || prev.prompt_template,
        description: d.description_clean || prev.description || nlDescription,
      }));
      toast.success("AI populated the form — review and save");
    } catch (e) {
      toast.error("AI generation failed");
    } finally {
      setNlLoading(false);
    }
  };

  useEffect(() => {
    if (form.target_audience === "Custom segment" && allLeads.length === 0) {
      setLoadingLeads(true);
      axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setAllLeads(r.data || []))
        .catch(() => {})
        .finally(() => setLoadingLeads(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when audience mode changes; allLeads guard prevents refetch loop, token read at call-time
  }, [form.target_audience]);

  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const isEdit = !!agent?.id;

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="agent-builder-modal">
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.3)" }} onClick={onClose} />
      <div style={{
        position: "relative", width: 640, maxHeight: "85vh", background: "var(--surface)",
        borderRadius: 14, border: "0.5px solid var(--border)", boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{ padding: "18px 24px", borderBottom: "0.5px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: form.icon_color + "20", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {form.channel === "call" ? <Phone style={{ width: 16, height: 16, color: form.icon_color }} /> :
               form.channel === "sms" ? <MessageSquare style={{ width: 16, height: 16, color: form.icon_color }} /> :
               <Mail style={{ width: 16, height: 16, color: form.icon_color }} />}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 650 }}>{isEdit ? "Edit Agent" : "Create New Agent"}</div>
              <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>Configure your AI outreach agent</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="close-agent-builder">
            <X style={{ width: 18, height: 18, color: "var(--text-tertiary)" }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {/* Section 11C — Natural-language agent creation */}
          {!isEdit && (
            <div data-testid="agent-nl-panel" style={{ background: "#EEEDFE", border: "0.5px solid #CECBF6", borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#3C3489", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <Sparkles style={{ width: 13, height: 13 }} /> Describe what this agent should do
              </div>
              <textarea
                value={nlDescription}
                onChange={e => setNlDescription(e.target.value)}
                placeholder="e.g. 'Send a friendly SMS check-in to demo-stage cardiology leads who haven't been contacted in 5 days, and propose a Tuesday or Thursday slot.'"
                data-testid="agent-nl-description"
                style={{ width: "100%", minHeight: 60, padding: "8px 10px", fontSize: 12, borderRadius: 8, border: "0.5px solid #CECBF6", background: "#FAFAFE", outline: "none", resize: "vertical", fontFamily: "var(--font)" }}
              />
              <button
                onClick={generateAgentFromDescription}
                disabled={nlLoading || !nlDescription.trim()}
                data-testid="agent-nl-generate-btn"
                style={{ marginTop: 8, background: "#3C3489", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 500, cursor: (nlLoading || !nlDescription.trim()) ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 6, opacity: !nlDescription.trim() ? 0.55 : 1 }}>
                {nlLoading ? <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite" }} /> : <Sparkles style={{ width: 12, height: 12 }} />}
                {nlLoading ? "Generating…" : "Generate agent →"}
              </button>
              <span style={{ marginLeft: 10, fontSize: 11, color: "#3C3489" }}>or build manually below ↓</span>
            </div>
          )}

          {/* Name + Channel row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 4, display: "block" }}>Agent Name *</label>
              <input className="input" value={form.name} onChange={e => u("name", e.target.value)} placeholder="e.g., Weekend Follow-Up Agent" data-testid="agent-name-input" />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 4, display: "block" }}>Channel *</label>
              <div style={{ display: "flex", gap: 6 }}>
                {CHANNELS.map(ch => (
                  <button key={ch.id} onClick={() => u("channel", ch.id)} data-testid={`channel-${ch.id}`}
                    style={{
                      flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                      padding: "8px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer",
                      fontFamily: "var(--font)",
                      border: form.channel === ch.id ? `1.5px solid ${ch.color}` : "0.5px solid var(--border)",
                      background: form.channel === ch.id ? ch.color + "15" : "transparent",
                      color: form.channel === ch.id ? ch.color : "var(--text-secondary)",
                    }}>
                    <ch.icon style={{ width: 13, height: 13 }} />
                    {ch.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 4, display: "block" }}>Description</label>
            <textarea className="input" rows={2} value={form.description} onChange={e => u("description", e.target.value)}
              placeholder="What does this agent do? e.g., 'Sends personalized follow-up texts to leads after their first demo.'" data-testid="agent-description-input" />
          </div>

          {/* Tone + Icon Color */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 14, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 6, display: "block" }}>Tone / Voice</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {TONES.map(t => (
                  <button key={t} onClick={() => u("tone", t)} data-testid={`tone-${t.toLowerCase()}`}
                    style={{
                      padding: "4px 10px", fontSize: 11, borderRadius: 6, cursor: "pointer", fontFamily: "var(--font)",
                      border: form.tone === t ? "0.5px solid var(--blue)" : "0.5px solid var(--border)",
                      background: form.tone === t ? "var(--blue-light)" : "transparent",
                      color: form.tone === t ? "var(--blue)" : "var(--text-secondary)",
                      fontWeight: form.tone === t ? 600 : 400,
                    }}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 6, display: "block" }}>Color</label>
              <div style={{ display: "flex", gap: 4 }}>
                {["#0071e3", "#1a7a52", "#5448a8", "#b06a14", "#aa2a2a", "#1a7070", "#71706b"].map(c => (
                  <button key={c} onClick={() => u("icon_color", c)}
                    style={{
                      width: 24, height: 24, borderRadius: 6, background: c, border: form.icon_color === c ? "2px solid var(--text-primary)" : "2px solid transparent",
                      cursor: "pointer",
                    }} />
                ))}
              </div>
            </div>
          </div>

          {/* Prompt Template */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 4, display: "block" }}>
              {form.channel === "call" ? "Call Script / Prompt" : form.channel === "sms" ? "SMS Template / Prompt" : "Email Prompt / Template"}
            </label>
            <textarea className="input" rows={5} value={form.prompt_template} onChange={e => u("prompt_template", e.target.value)}
              placeholder={form.channel === "call"
                ? "Hi {{contact_name}}, this is {{agent_name}} from Enable Healthcare. I'm calling about {{topic}}..."
                : form.channel === "sms"
                ? "Hi {{contact_name}}, quick update from Enable Healthcare: {{message}}. Reply STOP to opt out."
                : "Draft a personalized {{tone}} email to {{contact_name}} at {{company}} about {{topic}}. Reference their specialty ({{specialty}}) and products of interest."
              }
              data-testid="agent-prompt-input"
              style={{ fontFamily: "monospace", fontSize: 12 }} />
            <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 4 }}>
              Variables: {"{{contact_name}}, {{company}}, {{specialty}}, {{products}}, {{lead_source}}, {{stage}}, {{days_stale}}, {{agent_name}}, {{topic}}"}
            </div>
          </div>

          {/* Trigger */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 6, display: "block" }}>Trigger</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {TRIGGERS.map(t => (
                <button key={t} onClick={() => u("trigger", t)} data-testid={`trigger-${t.replace(/[\s→()]+/g, '-').toLowerCase().slice(0, 20)}`}
                  style={{
                    padding: "4px 10px", fontSize: 10, borderRadius: 6, cursor: "pointer", fontFamily: "var(--font)",
                    border: form.trigger === t ? "0.5px solid var(--blue)" : "0.5px solid var(--border)",
                    background: form.trigger === t ? "var(--blue-light)" : "transparent",
                    color: form.trigger === t ? "var(--blue)" : "var(--text-secondary)",
                    fontWeight: form.trigger === t ? 600 : 400,
                  }}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Target Audience */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 6, display: "block" }}>Target Audience</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {TARGET_AUDIENCES.map(a => (
                <button key={a} onClick={() => u("target_audience", a)} data-testid={`audience-${a.replace(/[\s()]+/g, '-').toLowerCase().slice(0, 20)}`}
                  style={{
                    padding: "4px 10px", fontSize: 10, borderRadius: 6, cursor: "pointer", fontFamily: "var(--font)",
                    border: form.target_audience === a ? "0.5px solid var(--blue)" : "0.5px solid var(--border)",
                    background: form.target_audience === a ? "var(--blue-light)" : "transparent",
                    color: form.target_audience === a ? "var(--blue)" : "var(--text-secondary)",
                    fontWeight: form.target_audience === a ? 600 : 400,
                  }}>
                  {a}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Segment — Lead Search & Selection */}
          {form.target_audience === "Custom segment" && (
            <div style={{ marginBottom: 16, border: "0.5px solid var(--blue)", borderRadius: 10, padding: 14, background: "var(--blue-light)" + "40" }} data-testid="custom-segment-panel">
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 4, display: "block" }}>Segment Name</label>
                <input className="input" value={form.segment_name} onChange={e => u("segment_name", e.target.value)} placeholder="e.g., High-value cardiology leads" style={{ width: "100%", fontSize: 12 }} data-testid="segment-name" />
              </div>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 4, display: "block" }}>Search & select leads</label>
              <input className="input" value={leadSearch} onChange={e => setLeadSearch(e.target.value)} placeholder="Search by name, practice, specialty..." style={{ width: "100%", fontSize: 12, marginBottom: 8 }} data-testid="lead-search-input" />
              {/* Selected leads */}
              {form.selected_leads.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
                  {form.selected_leads.map((sl, i) => (
                    <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 6, background: "var(--blue-light)", color: "var(--blue)", fontSize: 10, fontWeight: 600 }}>
                      {sl.name || sl.title}
                      <button onClick={() => setForm(p => ({ ...p, selected_leads: p.selected_leads.filter((_, j) => j !== i) }))} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "var(--blue)", fontSize: 12, lineHeight: 1 }}>&times;</button>
                    </span>
                  ))}
                </div>
              )}
              {/* Lead results */}
              <div style={{ maxHeight: 160, overflowY: "auto", border: "0.5px solid var(--border)", borderRadius: 8, background: "#fff" }}>
                {loadingLeads ? <div style={{ padding: 12, textAlign: "center", fontSize: 11, color: "var(--text-tertiary)" }}>Loading leads...</div> : (
                  allLeads.filter(l => {
                    if (!leadSearch) return true;
                    const s = leadSearch.toLowerCase();
                    return (l.contact_name || "").toLowerCase().includes(s) || (l.title || "").toLowerCase().includes(s) || (l.specialty || "").toLowerCase().includes(s) || (l.company || "").toLowerCase().includes(s);
                  }).slice(0, 20).map(l => {
                    const isSelected = form.selected_leads.some(sl => sl.id === l.id);
                    return (
                      <div key={l.id} onClick={() => {
                        if (isSelected) setForm(p => ({ ...p, selected_leads: p.selected_leads.filter(sl => sl.id !== l.id) }));
                        else setForm(p => ({ ...p, selected_leads: [...p.selected_leads, { id: l.id, name: l.contact_name, title: l.title }] }));
                      }}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", cursor: "pointer", borderBottom: "0.5px solid #f5f5f5", background: isSelected ? "var(--blue-light)" : "transparent" }}
                        data-testid={`lead-option-${l.id}`}>
                        <input type="checkbox" checked={isSelected} readOnly style={{ width: 12, height: 12, accentColor: "var(--blue)" }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 600 }}>{l.contact_name || "—"}</div>
                          <div style={{ fontSize: 9, color: "var(--text-tertiary)" }}>{l.title} · {l.specialty || "General"}</div>
                        </div>
                        <div style={{ fontSize: 9, color: "var(--text-tertiary)" }}>{l.stage || ""}</div>
                      </div>
                    );
                  })
                )}
              </div>
              <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 6 }}>{form.selected_leads.length} lead{form.selected_leads.length !== 1 ? "s" : ""} selected</div>
            </div>
          )}
          {form.trigger === "Custom schedule" && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 4, display: "block" }}>Custom Schedule</label>
              <input className="input" value={form.schedule} onChange={e => u("schedule", e.target.value)} placeholder="e.g., Every weekday at 10 AM, Tuesdays and Thursdays at 2 PM" data-testid="agent-schedule-input" />
            </div>
          )}

          {/* Active toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <button onClick={() => u("active", !form.active)} data-testid="agent-active-toggle"
              style={{ width: 40, height: 22, borderRadius: 11, border: "none", cursor: "pointer", position: "relative", background: form.active ? "var(--green)" : "var(--gray-light)", transition: "background 0.2s" }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: form.active ? 20 : 2, transition: "left 0.2s" }} />
            </button>
            <span style={{ fontSize: 12, fontWeight: 560 }}>{form.active ? "Active — agent will run automatically" : "Paused — manual runs only"}</span>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "0.5px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button className="btn" onClick={onClose} style={{ fontSize: 12 }} data-testid="cancel-agent-btn">Cancel</button>
          <button className="btn btn-primary" onClick={() => { if (!form.name.trim()) { toast.error("Agent name is required"); return; } onSave(form); }}
            disabled={!form.name.trim()} style={{ fontSize: 12 }} data-testid="save-agent-btn">
            <Save style={{ width: 12, height: 12 }} /> {isEdit ? "Update Agent" : "Create Agent"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
export default function AIAgents() {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [presetAgents, setPresetAgents] = useState([]);
  const [customAgents, setCustomAgents] = useState([]);
  const [loadingCustom, setLoadingCustom] = useState(true);
  const [runningId, setRunningId] = useState(null);
  const [resultPanel, setResultPanel] = useState(null);
  const [queue, setQueue] = useState([]);
  const [showQueue, setShowQueue] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [editPanel, setEditPanel] = useState({ name: "", channel: "email", schedule: "Manual only", staleness: "Any", aiScore: "All", leadStage: "All stages", steps: [] });
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [activeTab, setActiveTab] = useState("all");

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchCustomAgents defined below; run once on mount
  useEffect(() => { fetchCustomAgents(); }, []);

  const fetchCustomAgents = async () => {
    try {
      const res = await axios.get(`${API}/agents`, { headers: { Authorization: `Bearer ${token}` } });
      setCustomAgents(res.data || []);
    } catch {}
    setLoadingCustom(false);
  };

  const togglePreset = (id) => setPresetAgents(a => a.map(x => x.id === id ? { ...x, active: !x.active } : x));

  const toggleCustom = async (agent) => {
    const updated = { ...agent, active: !agent.active };
    setCustomAgents(prev => prev.map(a => a.id === agent.id ? { ...a, active: !a.active } : a));
    try {
      await axios.put(`${API}/agents/${agent.id}`, updated, { headers: { Authorization: `Bearer ${token}` } });
    } catch { toast.error("Failed to update agent"); }
  };

  const runAgent = async (id) => {
    setRunningId(id);
    setResultPanel(null);
    try {
      const res = await axios.post(`${API}/ai/agent/run`, { agent_id: id }, { headers: { Authorization: `Bearer ${token}` } });
      setResultPanel(res.data);
      toast.success("Agent completed");
    } catch { toast.error("Agent execution failed"); }
    setRunningId(null);
  };

  const saveAgent = async (form) => {
    try {
      if (editingAgent?.id) {
        await axios.put(`${API}/agents/${editingAgent.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Agent updated");
      } else {
        await axios.post(`${API}/agents`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Agent created");
      }
      fetchCustomAgents();
      setShowBuilder(false);
      setEditingAgent(null);
    } catch { toast.error("Failed to save agent"); }
  };

  const deleteAgent = async (id) => {
    const ok = await confirm({
      title: "Delete this agent?",
      body: "The agent will stop running and its draft queue will be cleared. This cannot be undone.",
      confirmLabel: "Delete agent",
      confirmTone: "danger",
      testid: "agent-delete-confirm",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/agents/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setCustomAgents(prev => prev.filter(a => a.id !== id));
      toast.success("Agent deleted");
    } catch { toast.error("Failed to delete agent"); }
  };

  const approveItem = (qId) => { setQueue(q => q.filter(x => x.id !== qId)); toast.success("Approved & sent"); };
  const rejectItem = (qId) => { setQueue(q => q.filter(x => x.id !== qId)); toast.info("Draft rejected"); };
  const approveAll = () => { setQueue([]); toast.success(`Approved & sent ${queue.length} messages`); };

  const totalPresetActive = presetAgents.filter(a => a.active).length;
  const totalCustomActive = customAgents.filter(a => a.active).length;

  const selectAgent = (agent) => {
    setSelectedAgent(agent);
    setEditPanel({
      name: agent.name || "", channel: agent.channel || "email",
      schedule: agent.trigger || agent.schedule || "Manual only",
      staleness: "Any", aiScore: "All", leadStage: "All stages",
      steps: agent.actions || ["Check trigger condition", "Analyze lead context", "Generate & queue message"],
    });
  };

  const channelIcon = (ch) => {
    if (ch === "call") return Phone;
    if (ch === "sms") return MessageSquare;
    return Mail;
  };

  const filteredPresets = activeTab === "all" ? presetAgents :
    activeTab === "custom" ? [] :
    presetAgents.filter(a => a.channel === activeTab);

  const filteredCustom = activeTab === "all" ? customAgents :
    activeTab === "custom" ? customAgents :
    customAgents.filter(a => a.channel === activeTab);

  return (
    <div data-testid="ai-agents-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">AI Agents</h1>
          <p className="page-subtitle">Automated workflows powered by Claude AI</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditingAgent(null); setShowBuilder(true); }} data-testid="create-agent-btn">
          <Plus style={{ width: 13, height: 13 }} /> Create Agent
        </button>
      </div>

      <div className="page-content">
        <div style={{ display: "flex", gap: 0 }}>
        {/* ── Left Column: Agent List ── */}
        <div style={{ flex: 1, minWidth: 0, paddingRight: selectedAgent ? 16 : 0, transition: "padding 0.2s" }}>
        {/* Pending review banner */}
        {queue.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "var(--amber-light)", border: "0.5px solid rgba(176,106,20,0.15)", borderRadius: 8, marginBottom: 12 }} data-testid="pending-banner">
            <Clock style={{ width: 14, height: 14, color: "var(--amber)" }} />
            <span style={{ fontSize: 12, color: "var(--amber)", flex: 1 }}><strong>{queue.length} messages</strong> pending your review — agents have drafted these and are waiting for approval.</span>
            <button className="btn" style={{ fontSize: 11, color: "var(--amber)", borderColor: "var(--amber)" }} onClick={() => setShowQueue(!showQueue)} data-testid="review-now-btn">
              {showQueue ? "Hide queue" : "Review now"}
            </button>
          </div>
        )}

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 14 }}>
          {[
            { label: "Preset agents", value: presetAgents.length },
            { label: "Custom agents", value: customAgents.length },
            { label: "Active", value: totalPresetActive + totalCustomActive },
            { label: "Pending approval", value: queue.length },
          ].map((s, i) => (
            <div key={i} className="stat-card"><div className="stat-card-value">{s.value}</div><div className="stat-card-label">{s.label}</div></div>
          ))}
        </div>

        {/* Channel tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 14 }}>
          {[
            { id: "all", label: "All Agents" },
            { id: "email", label: "Email" },
            { id: "sms", label: "SMS / Text" },
            { id: "call", label: "Call" },
            { id: "custom", label: "My Custom" },
          ].map(t => (
            <button key={t.id} className={`btn ${activeTab === t.id ? "btn-primary" : ""}`} onClick={() => setActiveTab(t.id)}
              data-testid={`agent-tab-${t.id}`}
              style={{ fontSize: 11, padding: "5px 12px" }}>
              {t.label}
              {t.id === "custom" && customAgents.length > 0 && <span className="tag tag-blue" style={{ fontSize: 9, marginLeft: 4 }}>{customAgents.length}</span>}
            </button>
          ))}
        </div>

        {/* Approval Queue */}
        {showQueue && queue.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 650 }}>Pending review <span className="tag tag-amber">{queue.length}</span></div>
              <button className="btn" style={{ fontSize: 10, color: "var(--amber)", borderColor: "var(--amber)" }} onClick={approveAll} data-testid="approve-all-btn">Approve all</button>
            </div>
            {queue.map(q => (
              <div key={q.id} className="card" style={{ padding: 0, marginBottom: 8, overflow: "hidden" }} data-testid={`queue-${q.id}`}>
                <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, borderBottom: "0.5px solid var(--border)" }}>
                  <Bot style={{ width: 14, height: 14, color: "var(--purple)" }} />
                  <span style={{ fontSize: 12, fontWeight: 560 }}>{q.leadName}</span>
                  <span style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{q.company}</span>
                  <span className="tag tag-purple" style={{ marginLeft: "auto", fontSize: 9 }}>{q.agentName}</span>
                  <span style={{ fontSize: 9, color: "var(--text-tertiary)" }}>{q.generatedAgo} ago</span>
                </div>
                <div style={{ padding: "10px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
                    <span className="tag tag-blue" style={{ fontSize: 9 }}>{q.channel}</span>
                    {q.subject && <span style={{ fontSize: 11, fontWeight: 560 }}>{q.subject}</span>}
                  </div>
                  <p style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" }}>{q.preview}</p>
                </div>
                <div style={{ padding: "8px 14px", borderTop: "0.5px solid var(--border)", display: "flex", gap: 6 }}>
                  <button className="btn" style={{ fontSize: 11 }}>Edit & Send</button>
                  <button className="btn btn-primary" style={{ fontSize: 11 }} onClick={() => approveItem(q.id)}>Approve & Send</button>
                  <button className="btn" style={{ fontSize: 11, color: "var(--red)", borderColor: "var(--red)" }} onClick={() => rejectItem(q.id)}>Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Result Panel */}
        {resultPanel && (
          <div className="card" style={{ marginBottom: 14, padding: "14px 18px", background: "var(--green-light)", border: "0.5px solid rgba(26,122,82,0.15)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <CheckCircle2 style={{ width: 14, height: 14, color: "var(--green)" }} />
                <span style={{ fontSize: 13, fontWeight: 650 }}>{resultPanel.agent_id} — {resultPanel.status}</span>
              </div>
              <button onClick={() => setResultPanel(null)} style={{ background: "none", border: "none", cursor: "pointer" }}><X style={{ width: 14, height: 14, color: "var(--text-tertiary)" }} /></button>
            </div>
            <p style={{ fontSize: 12, color: "var(--text-secondary)" }}>{resultPanel.summary}</p>
            {resultPanel.results && <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 6 }}>{resultPanel.results.slice(0, 8).map((r, i) => <span key={i} className={`tag ${r.temp === "Hot" ? "tag-red" : r.temp === "Warm" ? "tag-amber" : "tag-blue"}`} style={{ fontSize: 10 }}>{r.title}: {r.score}</span>)}</div>}
          </div>
        )}

        {/* Custom Agents Section */}
        {filteredCustom.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 650, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <Settings2 style={{ width: 14, height: 14, color: "var(--blue)" }} />
              Your Custom Agents
              <span className="tag tag-blue" style={{ fontSize: 9 }}>{filteredCustom.length}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {filteredCustom.map(agent => {
                const ChIcon = channelIcon(agent.channel);
                return (
                  <div key={agent.id} className="card" style={{ padding: "16px 18px", cursor: "pointer", border: selectedAgent?.id === agent.id ? "1.5px solid #2563eb" : "0.5px solid var(--border)", transition: "border-color 0.15s" }}
                    onClick={() => selectAgent(agent)} data-testid={`custom-agent-${agent.id}`}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: (agent.icon_color || "#0071e3") + "20", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <ChIcon style={{ width: 16, height: 16, color: agent.icon_color || "#0071e3" }} />
                        </div>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 13, fontWeight: 650 }}>{agent.name}</span>
                            <span className={`tag ${agent.active ? "tag-green" : "tag-gray"}`} style={{ fontSize: 9 }}>{agent.active ? "Active" : "Paused"}</span>
                            <span className="tag tag-blue" style={{ fontSize: 9 }}>{agent.channel}</span>
                          </div>
                          <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{agent.trigger || "Manual"}</div>
                        </div>
                      </div>
                      <button onClick={() => toggleCustom(agent)} data-testid={`toggle-custom-${agent.id}`}
                        style={{ width: 36, height: 20, borderRadius: 10, border: "none", cursor: "pointer", position: "relative", background: agent.active ? "var(--green)" : "var(--gray-light)", transition: "background 0.2s" }}>
                        <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: agent.active ? 18 : 2, transition: "left 0.2s" }} />
                      </button>
                    </div>
                    <p style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 8 }}>{agent.description || "No description"}</p>
                    {agent.tone && <div style={{ marginBottom: 8 }}><span className="tag tag-purple" style={{ fontSize: 9 }}>Tone: {agent.tone}</span></div>}
                    {agent.target_audience && <div style={{ marginBottom: 8 }}><span className="tag tag-gray" style={{ fontSize: 9 }}>{agent.target_audience}</span></div>}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 10, color: "var(--text-tertiary)", paddingTop: 8, borderTop: "0.5px solid var(--border)" }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button className="btn" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => { setEditingAgent(agent); setShowBuilder(true); }} data-testid={`edit-custom-${agent.id}`}>
                          <Edit3 style={{ width: 10, height: 10 }} /> Edit
                        </button>
                        <button className="btn" style={{ fontSize: 10, padding: "3px 8px", color: "var(--red)", borderColor: "var(--red)" }} onClick={() => deleteAgent(agent.id)} data-testid={`delete-custom-${agent.id}`}>
                          <Trash2 style={{ width: 10, height: 10 }} /> Delete
                        </button>
                      </div>
                      <button className="btn btn-primary" style={{ fontSize: 10, padding: "3px 10px" }} onClick={() => runAgent(agent.id)} disabled={runningId === agent.id} data-testid={`run-custom-${agent.id}`}>
                        {runningId === agent.id ? <Loader2 style={{ width: 10, height: 10, animation: "spin 1s linear infinite" }} /> : <Play style={{ width: 10, height: 10 }} />}
                        {runningId === agent.id ? "Running..." : "Run now"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Preset Agents Section */}
        {filteredPresets.length > 0 && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 650, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <Zap style={{ width: 14, height: 14, color: "var(--amber)" }} />
              {activeTab === "custom" ? "" : "Built-in Agents"}
              <span className="tag tag-gray" style={{ fontSize: 9 }}>{filteredPresets.length}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {filteredPresets.map(agent => {
                const Icon = ICON_MAP[agent.icon] || Bot;
                return (
                  <div key={agent.id} className="card" style={{ padding: "16px 18px", cursor: "pointer", border: selectedAgent?.id === agent.id ? "1.5px solid #2563eb" : "0.5px solid var(--border)", transition: "border-color 0.15s" }}
                    onClick={() => selectAgent(agent)} data-testid={`agent-${agent.id}`}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: agent.iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Icon style={{ width: 16, height: 16, color: agent.iconColor }} />
                        </div>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: 13, fontWeight: 650 }}>{agent.name}</span>
                            <span className={`tag ${agent.active ? "tag-green" : "tag-gray"}`} style={{ fontSize: 9 }}>{agent.active ? "Active" : "Paused"}</span>
                          </div>
                          <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{agent.trigger}</div>
                        </div>
                      </div>
                      <button onClick={() => togglePreset(agent.id)} data-testid={`toggle-${agent.id}`}
                        style={{ width: 36, height: 20, borderRadius: 10, border: "none", cursor: "pointer", position: "relative", background: agent.active ? "var(--green)" : "var(--gray-light)", transition: "background 0.2s" }}>
                        <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: agent.active ? 18 : 2, transition: "left 0.2s" }} />
                      </button>
                    </div>
                    <p style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 8 }}>{agent.description}</p>
                    <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginBottom: 8 }}>
                      {agent.actions.map((a, i) => <span key={i} className={`tag tag-${agent.tagColor}`} style={{ fontSize: 9 }}>{a}</span>)}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 10, color: "var(--text-tertiary)", paddingTop: 8, borderTop: "0.5px solid var(--border)" }}>
                      <span>Last run: {agent.lastRun} {agent.successRate > 0 && `· ${agent.successRate}%`}</span>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button className="btn" style={{ fontSize: 10, padding: "3px 10px" }} onClick={(e) => { e.stopPropagation(); selectAgent(agent); }} data-testid={`customize-${agent.id}`}>
                          Customize &rarr;
                        </button>
                        <button className="btn btn-primary" style={{ fontSize: 10, padding: "3px 10px" }} onClick={(e) => { e.stopPropagation(); runAgent(agent.id); }} disabled={!agent.active || runningId === agent.id} data-testid={`run-${agent.id}`}>
                          {runningId === agent.id ? <Loader2 style={{ width: 10, height: 10, animation: "spin 1s linear infinite" }} /> : <Play style={{ width: 10, height: 10 }} />}
                          {runningId === agent.id ? "Running..." : "Run now"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state for custom tab */}
        {activeTab === "custom" && customAgents.length === 0 && !loadingCustom && (
          <div className="card" style={{ padding: 40, textAlign: "center" }}>
            <Bot style={{ width: 32, height: 32, color: "var(--text-tertiary)", margin: "0 auto 8px" }} />
            <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 12 }}>No custom agents yet</p>
            <button className="btn btn-primary" onClick={() => { setEditingAgent(null); setShowBuilder(true); }} data-testid="create-first-agent-btn">
              <Plus style={{ width: 12, height: 12 }} /> Create your first agent
            </button>
          </div>
        )}

        {/* Real-mode empty state for "All" tab when no agents exist at all */}
        {activeTab === "all" && filteredPresets.length === 0 && filteredCustom.length === 0 && !loadingCustom && (
          <div className="card" style={{ padding: 40, textAlign: "center" }} data-testid="ai-agents-empty">
            <Bot style={{ width: 32, height: 32, color: "var(--text-tertiary)", margin: "0 auto 8px" }} />
            <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 12 }}>No AI agents configured yet</p>
            <button className="btn btn-primary" onClick={() => { setEditingAgent(null); setShowBuilder(true); }} data-testid="create-first-agent-btn-all">
              <Plus style={{ width: 12, height: 12 }} /> Create your first agent
            </button>
          </div>
        )}
        </div>

        {/* ── Right Column: Edit Panel ── */}
        {selectedAgent && (
          <div style={{ width: 360, flexShrink: 0, borderLeft: "0.5px solid #eee", background: "#fafafa", padding: 16, overflowY: "auto", maxHeight: "calc(100vh - 120px)" }} data-testid="agent-edit-panel">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ fontSize: 15, fontWeight: 500 }}>{selectedAgent.name}</div>
              <button onClick={() => setSelectedAgent(null)} style={{ background: "none", border: "none", cursor: "pointer" }}><X style={{ width: 16, height: 16, color: "#888" }} /></button>
            </div>
            {!selectedAgent.id?.startsWith?.("q") && !["welcome","demo_confirm","demo_reschedule","stale_reengagement","post_demo","proposal_nudge","closed_won","closed_lost","quote_expiry"].includes(selectedAgent.id) ? null : (
              <div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>Customizing this built-in agent saves as your own version</div>
            )}

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 5, display: "block" }}>Agent name</label>
              <input value={editPanel.name} onChange={e => setEditPanel(p => ({ ...p, name: e.target.value }))} style={{ width: "100%", padding: "7px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }} data-testid="edit-agent-name" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 5, display: "block" }}>Channel</label>
              <select value={editPanel.channel} onChange={e => setEditPanel(p => ({ ...p, channel: e.target.value }))} style={{ width: "100%", padding: "7px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }} data-testid="edit-agent-channel">
                <option value="email">Email</option><option value="sms">SMS / Text</option><option value="call">Call reminder</option>
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: "#888", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 5, display: "block" }}>Schedule</label>
              <select value={editPanel.schedule} onChange={e => setEditPanel(p => ({ ...p, schedule: e.target.value }))} style={{ width: "100%", padding: "7px 10px", border: "0.5px solid #ddd", borderRadius: 8, fontSize: 12, fontFamily: "inherit" }} data-testid="edit-agent-schedule">
                <option>Every Friday 3:00 PM</option><option>Every Monday 9:00 AM</option><option>Manual only</option><option>Custom...</option>
              </select>
            </div>

            <div style={{ width: "100%", height: "0.5px", background: "#eee", margin: "12px 0" }} />

            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Target leads — which leads should this run for?</div>
            {[
              { label: "Staleness", key: "staleness", options: ["Any", "7+ days", "14+ days", "30+ days"] },
              { label: "AI Score", key: "aiScore", options: ["All", "Hot 70+", "Warm 40–69", "Cold under 40"] },
              { label: "Lead stage", key: "leadStage", options: ["All stages", "New", "Contacted", "Demo", "Proposal", "Decision"] },
            ].map(group => (
              <div key={group.key} style={{ border: "0.5px solid #eee", borderRadius: 8, padding: 10, background: "#fff", marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>{group.label}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {group.options.map(opt => (
                    <button key={opt} onClick={() => setEditPanel(p => ({ ...p, [group.key]: opt }))}
                      style={{ padding: "3px 9px", borderRadius: 12, fontSize: 11, cursor: "pointer", fontFamily: "inherit",
                        border: editPanel[group.key] === opt ? "none" : "0.5px solid #ccc",
                        background: editPanel[group.key] === opt ? "#2563eb" : "transparent",
                        color: editPanel[group.key] === opt ? "#fff" : "#888",
                      }}>{opt}</button>
                  ))}
                </div>
              </div>
            ))}

            <div style={{ width: "100%", height: "0.5px", background: "#eee", margin: "12px 0" }} />

            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Agent flow steps</div>
            {(editPanel.steps || []).map((step, i) => (
              <div key={i} style={{ border: "0.5px solid #eee", borderRadius: 8, padding: "8px 12px", background: "#fff", marginBottom: 6, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#E6F1FB", color: "#185FA5", fontSize: 10, fontWeight: 500, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</div>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{step}</div>
              </div>
            ))}
            <div style={{ border: "0.5px dashed #bbb", borderRadius: 8, padding: "8px 12px", textAlign: "center", color: "#888", fontSize: 12, cursor: "pointer", marginBottom: 14 }}
              onClick={() => setEditPanel(p => ({ ...p, steps: [...(p.steps || []), "New step"] }))}>+ Add step</div>

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setSelectedAgent(null)} style={{ flex: 1, padding: "7px 0", borderRadius: 8, border: "0.5px solid #ccc", background: "#fff", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Discard</button>
              <button onClick={() => { toast.success(`${editPanel.name} saved`); setSelectedAgent(null); }} style={{ flex: 2, padding: "7px 0", borderRadius: 8, border: "none", background: "#2563eb", color: "#fff", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }} data-testid="save-agent-panel">Save agent</button>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Agent Builder Modal */}
      {showBuilder && (
        <AgentBuilderModal agent={editingAgent} onSave={saveAgent} onClose={() => { setShowBuilder(false); setEditingAgent(null); }} />
      )}
    </div>
  );
}
